import {GameStatusMocks} from '../GameStatusService/GameStatusService.mocks';
import {DI_TOKENS} from '@/di/di.tokens';
import {GameStatusType} from '@/stores/GameStatusStore/GameStatusStore.constants';
import {TaskExecutionRules, TaskStatus, TaskSubtaskType, TaskType} from '@/stores/TasksStore/TasksStore.constants';
import {Task, TaskReward, TaskSubtask} from '@/stores/TasksStore/TasksStore.types';

import {injected} from 'brandi';

import {ArrayOperations} from '@/utils/ArrayOperations';
import {RealRandom} from '@/utils/random/RealRandom';

export class TasksMocks {
  public tasks: Task[];

  constructor(private readonly gameStatusMocks: GameStatusMocks) {
    this.tasks = this.gameStatusMocks.getStatus() === GameStatusType.Mocks ? MOCKS_STATUS_TASK_LIST : createTasksList();
  }

  public claimReward(taskId: string): TaskReward {
    const subtaskIds = this.getSubtaskIdsForClaimReward(taskId);
    this.updateTasksStatusAfterClaimReward(taskId, subtaskIds);

    return this.getTotalRewardAfterClaimRewards(taskId, subtaskIds);
  }

  private updateTasksStatusAfterClaimReward = (taskId: string, subtaskIds: number[]): void => {
    let newStatus: TaskStatus | null = null;

    const updatedTasks = this.tasks.map((task) => {
      if (task.taskId === taskId) {
        const updatedSubtasks = task.subtasks.map((subtask) => {
          if (subtaskIds.includes(subtask.id)) {
            return {
              ...subtask,
              status: TaskStatus.Done,
            };
          }
          return subtask;
        });

        const isTaskDone = updatedSubtasks.every((t) => t.status === TaskStatus.Done);
        newStatus = isTaskDone ? TaskStatus.Done : task.status;

        return {
          ...task,
          status: newStatus,
          completedAt: isTaskDone ? new Date().toISOString() : task.completedAt,
          subtasks: updatedSubtasks,
        };
      }

      return task;
    });

    this.tasks = this.sortTasksByCanClaimReward(updatedTasks);
  };

  private sortTasksByCanClaimReward = (tasks: Task[]): Task[] => {
    const canClaimRewardTaskList: Task[] = [];
    const otherTaskList: Task[] = [];

    for (const task of tasks) {
      if (task.subtasks.some((subtask) => this.isSubtaskCanClaimReward(subtask))) {
        canClaimRewardTaskList.push(task);
        continue;
      }
      otherTaskList.push(task);
    }

    return [...canClaimRewardTaskList, ...otherTaskList];
  };

  private getSubtaskIdsForClaimReward = (taskId: string): number[] => {
    const task = this.tasks.find((t) => t.taskId === taskId);

    if (!task) return [];

    return task.subtasks.reduce<number[]>((acc, subtask) => {
      if (this.isSubtaskCanClaimReward(subtask)) {
        acc.push(subtask.id);
      }
      return acc;
    }, []);
  };

  private getTotalRewardAfterClaimRewards = (taskId: string, subtaskIds: number[]): TaskReward => {
    const task = this.tasks.find((t) => t.taskId === taskId);

    const resultReward: TaskReward = {
      energyRecharge: 0,
      multiTap: 0,
    };

    if (!task) return resultReward;

    task.subtasks.forEach((t) => {
      if (subtaskIds.includes(t.id)) {
        resultReward.energyRecharge += t.reward.energyRecharge;
        resultReward.multiTap += t.reward.multiTap;
      }
    });

    if (task.status === TaskStatus.Done && task.subtasks.length > 1) {
      resultReward.energyRecharge += task.reward.energyRecharge;
      resultReward.multiTap += task.reward.multiTap;
    }

    return resultReward;
  };

  private isSubtaskCanClaimReward = (subtask: TaskSubtask): boolean => {
    return subtask.canClaimReward && subtask.status === TaskStatus.InProgress;
  };
}

injected(TasksMocks, DI_TOKENS.gameStatusMocks);

// фиксированные моки для завершенного проекта
const MOCKS_STATUS_TASK_LIST: Task[] = [
  {
    taskId: '1',
    title: 'Выполненное задание 1',
    description: 'Забирайте награду',
    reward: {
      energyRecharge: 2,
      multiTap: 3,
    },
    wasSeen: true,
    status: TaskStatus.InProgress,
    type: TaskType.Default,
    completedAt: new Date().toISOString(),
    executionRules: [],
    isBlocked: false,
    isExpired: false,
    subtasks: [
      {
        title: 'Выполненное задание',
        description: 'Забирайте награду',
        id: 1,
        status: TaskStatus.InProgress,
        cta: null,
        completedAt: new Date().toISOString(),
        reward: {
          energyRecharge: 2,
          multiTap: 3,
        },
        canClaimReward: true,
        type: TaskSubtaskType.ClickLink,
        withDelay: false,
      },
    ],
  },
  {
    taskId: '2',
    title: 'Выполненное задание 2',
    description: 'Забирайте награду',
    reward: {
      energyRecharge: 2,
      multiTap: 3,
    },
    wasSeen: true,
    status: TaskStatus.InProgress,
    type: TaskType.Default,
    completedAt: new Date().toISOString(),
    executionRules: [],
    isBlocked: false,
    isExpired: false,
    subtasks: [
      {
        title: 'Выполненное задание',
        description: 'Забирайте награду',
        id: 1,
        status: TaskStatus.InProgress,
        cta: null,
        completedAt: new Date().toISOString(),
        reward: {
          energyRecharge: 2,
          multiTap: 3,
        },
        canClaimReward: true,
        type: TaskSubtaskType.ClickLink,
        withDelay: false,
      },
    ],
  },
];

export const createTasksList = (): Task[] => {
  return [
    ...ArrayOperations.range(1, 2).map((index) => createTask({index, taskType: RandomTaskType.Blocked})),
    ...ArrayOperations.range(3, 4).map((index) => createTask({index, taskType: RandomTaskType.SimpleCanClaimReward})),
    ...ArrayOperations.range(5, 6).map((index) => createTask({index, taskType: RandomTaskType.Combo})),
    ...ArrayOperations.range(7, 10).map((index) => createTask({index, taskType: RandomTaskType.Simple})),
    ...ArrayOperations.range(11, 12).map((index) =>
      createTask({index, taskType: RandomTaskType.Simple, wasSeen: false}),
    ),
    createTask({index: 14, taskType: RandomTaskType.ComboInProgress, wasSeen: false, withDelay: true}),
    createTask({index: 15, taskType: RandomTaskType.Expired}),
    createTask({index: 16, taskType: RandomTaskType.ComboInProgress, isFriendInvitation: true}),
    createTask({index: 17, taskType: RandomTaskType.Simple, isClickLink: true}),
  ];
};

enum RandomTaskType {
  Blocked = 'blocked',
  Expired = 'expired',
  SimpleCanClaimReward = 'simpleCanClaimReward',
  Simple = 'simple',
  ComboCanClaimReward = 'ComboCanClaimReward',
  ComboInProgress = 'comboInProgress',
  Combo = 'combo',
}

interface CreateTaskProps {
  index: number;
  taskType?: RandomTaskType;
  wasSeen?: boolean;
  withDelay?: boolean;
  isFriendInvitation?: boolean;
  isClickLink?: boolean;
}

export const createTask = ({
  index,
  taskType,
  wasSeen = true,
  withDelay,
  isFriendInvitation,
  isClickLink,
}: CreateTaskProps): Task => {
  const realRandom = new RealRandom();
  const randomTaskType = taskType
    ? taskType
    : realRandom.choice([
        RandomTaskType.Blocked,
        RandomTaskType.Expired,
        RandomTaskType.SimpleCanClaimReward,
        RandomTaskType.Simple,
        RandomTaskType.ComboCanClaimReward,
        RandomTaskType.Combo,
      ]);

  const getRandomExecutionRules = (): TaskExecutionRules[] => [
    realRandom.choice([
      TaskExecutionRules.HasBlackCard,
      TaskExecutionRules.HasBrokerAccount,
      TaskExecutionRules.HasInvestBox,
      TaskExecutionRules.HasPulseProfile,
      TaskExecutionRules.NoPulseProfile,
      TaskExecutionRules.OverEighteen,
      TaskExecutionRules.InvestRiskProfileIsDefined,
      TaskExecutionRules.InvestRiskProfileIsNotDefined,
    ]),
  ];

  const baseProps = {
    taskId: `t_${index}`,
    title: `Заголовок задания ${index}`,
    description: `Подзаголовок задания ${index}`,
    reward: {
      energyRecharge: realRandom.choice([1, 2]),
      multiTap: realRandom.choice([15, 30, 45]),
    },
    wasSeen,
    status: TaskStatus.InProgress,
    type: TaskType.Default,
    completedAt: null,
  };

  const baseSubtaskProps = {
    realRandom,
    taskIndex: index,
    withDelay,
    isFriendInvitation,
    isClickLink,
  };

  switch (randomTaskType) {
    case RandomTaskType.Blocked:
      return {
        ...baseProps,
        isBlocked: true,
        isExpired: false,
        executionRules: getRandomExecutionRules(),
        subtasks: [
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 1,
            canClaimReward: false,
          }),
        ],
      };
    case RandomTaskType.Expired:
      return {
        ...baseProps,
        isBlocked: false,
        isExpired: true,
        executionRules: getRandomExecutionRules(),
        subtasks: [
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 1,
            canClaimReward: false,
          }),
        ],
      };
    case RandomTaskType.SimpleCanClaimReward:
      return {
        ...baseProps,
        isBlocked: false,
        isExpired: false,
        executionRules: [],
        subtasks: [
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 1,
            canClaimReward: true,
          }),
        ],
      };
    case RandomTaskType.Simple:
      return {
        ...baseProps,
        isBlocked: false,
        isExpired: false,
        executionRules: [],
        subtasks: [
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 1,
            canClaimReward: false,
          }),
        ],
      };
    case RandomTaskType.ComboCanClaimReward:
      return {
        ...baseProps,
        isBlocked: false,
        isExpired: false,
        executionRules: [],
        subtasks: [
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 1,
            canClaimReward: true,
          }),
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 2,
            canClaimReward: true,
          }),
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 3,
            canClaimReward: true,
          }),
        ],
      };
    case RandomTaskType.ComboInProgress:
      return {
        ...baseProps,
        isBlocked: false,
        isExpired: false,
        executionRules: [],
        subtasks: [
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 1,
            canClaimReward: false,
          }),
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 2,
            canClaimReward: false,
          }),
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 3,
            canClaimReward: false,
          }),
        ],
      };
    case RandomTaskType.Combo:
      return {
        ...baseProps,
        isBlocked: false,
        isExpired: false,
        executionRules: [],
        subtasks: [
          createSubtask({...baseSubtaskProps, subtaskIndex: 1}),
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 2,
            canClaimReward: false,
          }),
          createSubtask({
            ...baseSubtaskProps,
            subtaskIndex: 3,
            canClaimReward: false,
          }),
        ],
      };
  }
};

enum RandomSubtaskTaskType {
  CanClaimReward = 'canClaimReward',
  InProgress = 'inProgress',
  Done = 'done',
}

interface CreateSubtaskProps {
  realRandom: RealRandom;
  taskIndex: number;
  subtaskIndex: number;
  canClaimReward?: boolean;
  withDelay?: boolean;
  isFriendInvitation?: boolean;
  isClickLink?: boolean;
}

const createSubtask = ({
  realRandom,
  taskIndex,
  subtaskIndex,
  canClaimReward,
  withDelay,
  isFriendInvitation,
  isClickLink,
}: CreateSubtaskProps): TaskSubtask => {
  let randomSubtaskType = realRandom.choice([
    RandomSubtaskTaskType.CanClaimReward,
    RandomSubtaskTaskType.InProgress,
    RandomSubtaskTaskType.Done,
  ]);

  if (canClaimReward !== undefined) {
    randomSubtaskType = canClaimReward ? RandomSubtaskTaskType.CanClaimReward : RandomSubtaskTaskType.InProgress;
  }

  const getRandomSubtaskType = (): TaskSubtaskType => {
    if (isFriendInvitation) {
      return TaskSubtaskType.FriendInvitation;
    }

    if (isClickLink) {
      return TaskSubtaskType.ClickLink;
    }

    return realRandom.choice([
      TaskSubtaskType.InvestAcademyLessonPassed,
      TaskSubtaskType.InvestAccountCreation,
      TaskSubtaskType.InvestboxCreation,
      TaskSubtaskType.PulsePostCommentCreation,
      TaskSubtaskType.PulsePostCreation,
      TaskSubtaskType.PulseProfileSubscription,
      TaskSubtaskType.PulseReaction,
    ]);
  };

  const type = getRandomSubtaskType();
  let title = `Задание ${taskIndex} подзадание ${subtaskIndex}`;
  let ctaText = `cta к подзаданию ${subtaskIndex}`;

  if (type === TaskSubtaskType.FriendInvitation) {
    title = `Пригласить друга, подзадание ${subtaskIndex}`;
    ctaText = `Ссылка-приглашение`;
  }

  const baseProps = {
    id: subtaskIndex,
    title,
    description: realRandom.choice([`Задание ${taskIndex} описание подзадания ${subtaskIndex}`, null]),
    reward: {
      energyRecharge: subtaskIndex === 1 ? realRandom.choice([1, 2]) : realRandom.choice([0, 1, 2]),
      multiTap: realRandom.choice([15, 30]),
    },
    type,
    withDelay: Boolean(withDelay),
  };

  switch (randomSubtaskType) {
    case RandomSubtaskTaskType.CanClaimReward:
      return {
        ...baseProps,
        status: TaskStatus.InProgress,
        cta: null,
        completedAt: null,
        canClaimReward: true,
      };
    case RandomSubtaskTaskType.InProgress:
      return {
        ...baseProps,
        status: TaskStatus.InProgress,
        cta: {
          text: ctaText,
          link: 'https://test.ru',
          iosLink: 'https://test.ru',
          deeplink: null,
          investAppDeeplink: null,
        },
        completedAt: null,
        canClaimReward: false,
      };
    case RandomSubtaskTaskType.Done:
      return {
        ...baseProps,
        status: TaskStatus.Done,
        cta: null,
        completedAt: '2024-04-28T13:44:45.612Z',
        canClaimReward: false,
      };
  }
};
